import { ChangeEvent, DragEvent, ReactNode, useState } from 'react';
import { IconFilePdf } from '@coverflex-tech/hypernova';
import { IFile } from '@coverflex-tech/hypernova-utils';
import classnames from 'classnames';
import AttachmentPreview from '../AttachmentPreview';
import AttachmentThumbnail from '../AttachmentThumbnail';
import css from './attachment-manager.module.css';

export interface IAttachmentsProps {
  title: string;
  description: ReactNode;
  attachments: IFile[];
  onChange: (newAttachments: IFile[]) => void;
  dropLabel: string;
  addLabel: string;
  attachmentsLimit?: number;
  previewClassName?: string;
  className?: string;
}

export const AttachmentManager = ({
  title,
  description,
  attachments,
  onChange,
  addLabel,
  dropLabel,
  attachmentsLimit,
  previewClassName,
  className,
}: IAttachmentsProps) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isDropActive, setIsDropActive] = useState(false);

  const onAddFiles = (files: FileList) => {
    const validFiles = Array.from(files)
      .filter(
        (file) => file.type === 'application/pdf' || file.type.includes('image')
      )
      .map((file) => ({
        name: file.name ?? '',
        url: URL.createObjectURL(file),
        blob: file,
      }));
    const newAttachments = [...attachments, ...validFiles];
    onChange(newAttachments);
    setActiveIdx(newAttachments.length - 1);
  };

  const onUploadAttachment = (evt: ChangeEvent<HTMLInputElement>) => {
    const files = evt.target.files;
    if (files && files.length) {
      onAddFiles(files);
    }
  };

  const onRemoveAttachment = (idx: number) => {
    onChange([...attachments.slice(0, idx), ...attachments.slice(idx + 1)]);
    if (idx > activeIdx) {
      return;
    }
    if (idx > 0 && idx < activeIdx) {
      setActiveIdx(activeIdx - 1);
      return;
    }
    setActiveIdx(0);
  };

  const onDropFile = (evt: DragEvent<HTMLLabelElement>) => {
    evt.preventDefault();
    setIsDropActive(false);
    const droppedFiles = evt.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length) {
      onAddFiles(droppedFiles);
    }
  };

  const onDragOver = (evt: DragEvent<HTMLLabelElement>) => {
    evt.preventDefault();

    if (!isDropActive) {
      setIsDropActive(true);
    }
  };

  const onDragLeave = (evt: DragEvent<HTMLLabelElement>) => {
    if (evt.target === evt.currentTarget) {
      setIsDropActive(false);
    }
  };

  const activeAttachment = attachments[activeIdx];
  const showPlaceholder = attachments.length === 0 || !activeAttachment;

  return (
    <div className={classnames(css.host, className)}>
      <div
        className={classnames(
          css.container,
          {
            [css.emptyContainer!]: showPlaceholder,
          },
          previewClassName
        )}
      >
        {showPlaceholder ? (
          <label
            className={classnames(css.placeholderLabel, {
              [css.dropActive!]: isDropActive,
            })}
            onDrop={onDropFile}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          >
            {isDropActive ? (
              <div className={css.dropLabel}>{dropLabel}</div>
            ) : (
              <>
                <div className={css.iconHolder}>
                  <IconFilePdf />
                </div>
                <div className={css.placeholderTitle}>{title}</div>
                <div className={css.placeholderDescription}>{description}</div>
                <input
                  type="file"
                  onChange={onUploadAttachment}
                  accept="application/pdf,image/*"
                  data-test-id="file_input"
                  hidden
                />
              </>
            )}
          </label>
        ) : (
          <AttachmentPreview
            attachment={activeAttachment}
            onRemove={() => onRemoveAttachment(activeIdx)}
          />
        )}
      </div>
      {attachments.length > 0 && attachmentsLimit !== 1 && (
        <div className={css.thumbnails}>
          {attachments.map((thumbnail, idx) => (
            <AttachmentThumbnail
              key={idx}
              attachment={thumbnail}
              onClick={() => setActiveIdx(idx)}
              selected={idx === activeIdx}
              onRemove={() => onRemoveAttachment(idx)}
            />
          ))}
          {(!attachmentsLimit || attachments.length < attachmentsLimit) && (
            <label className={css.addNewBtn}>
              <span>{addLabel}</span>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={onUploadAttachment}
                hidden
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
};
